/** The `DataSource` over HTTP: the exact routes of apps/api/src/routes/auth.ts and admin.ts. */
import type { PermissionDef } from "@faylasuf/contracts";
import { createApiClient, type ApiClient } from "./client.js";
import type { DataSource, LoginResult, SessionInfo } from "./types.js";

/** Query string of defined, non-empty values ("" when none). */
function qs(q: Readonly<Record<string, string | undefined>>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== "") p.set(k, v);
  const s = p.toString();
  return s ? `?${s}` : "";
}

/** [C34] Audit query string: `all` as "1". */
function auditQs(q: object): string {
  const { all, ...rest } = q as { all?: boolean } & Record<string, string | undefined>;
  return qs({ ...rest, ...(all ? { all: "1" } : {}) });
}

export function createApiSource(client: ApiClient = createApiClient()): DataSource {
  return {
    office: () => client.get("/office"),
    async login(username, password) {
      const r = await client.post<LoginResult>("/auth/login", { username, password });
      client.setCsrfToken(r.csrfToken);
      return r;
    },
    secondFactor: (code) => client.post("/auth/second-factor", { code }),
    totpEnrol: () => client.post("/auth/totp/enrol"),
    totpConfirm: (code) => client.post("/auth/totp/confirm", { code }),
    async session() {
      const s = await client.get<SessionInfo>("/auth/session");
      client.setCsrfToken(s.session.csrfToken);
      return s;
    },
    async logout() {
      await client.post("/auth/logout");
      client.setCsrfToken(null);
    },
    // [C24] the API answers { unread, items } with camel-case dates (services/notifications.ts); the shell reads the list (F-259)
    notifications: async () => {
      const r = await client.get<{ unread: number; items: Array<{ id: string; category: string; code: string; params: Record<string, unknown>; createdAt: string; readAt: string | null }> }>("/notifications");
      return r.items.map((n) => ({ id: n.id, category: n.category, code: n.code, params: n.params, created_at: n.createdAt, read_at: n.readAt }));
    },
    dismissNotification: (id) => client.post("/notifications/dismiss", id ? { id } : {}),
    rates: () => client.get("/rates"),
    users: () => client.get("/users"),
    roles: () => client.get("/roles"),
    permissions: () => client.get<readonly PermissionDef[]>("/permissions"),
    createUser: (body) => client.post("/users", body),
    updateUser: (body) => client.patch("/users", body),
    resetAccess: (userId, pin) => client.post("/users/reset-access", { userId, pin }),
    createRole: (body) => client.post("/roles", body),
    updateRole: (body) => client.patch("/roles", body),
    settings: () => client.get("/settings"),
    setSetting: (key, value, pin) => client.put("/settings", { key, value, pin }),
    preferences: () => client.get("/preferences"),
    setPreference: (key, value) => client.put("/preferences", { key, value }),
    changePin: (currentPin, newPin) => client.post("/security/pin/change", { currentPin, newPin }),
    resetPin: (userId, newPin, pin) => client.post("/security/pin/reset", { userId, newPin, pin }),
    securityTotpEnrol: (pin) => client.post("/security/totp/enrol", { pin }),
    securityTotpConfirm: (code, pin) => client.post("/security/totp/confirm", { code, pin }),
    setupStart: (token) => client.get(`/auth/setup/${encodeURIComponent(token)}`),
    setupComplete: (body) => client.post("/auth/setup/complete", body),
    // C19 (routes/accounts.ts, routes/operations.ts)
    accounts: (opts = {}) => {
      const q = new URLSearchParams();
      if (opts.kinds) q.set("kinds", opts.kinds.join(","));
      if (opts.archived) q.set("archived", "1");
      const qs = q.toString();
      return client.get(`/accounts${qs ? `?${qs}` : ""}`);
    },
    statement: (accountId, q) => {
      const p = new URLSearchParams();
      for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== "") p.set(k, String(v));
      const qs = p.toString();
      return client.get(`/accounts/${encodeURIComponent(accountId)}/statement${qs ? `?${qs}` : ""}`);
    },
    dashboard: () => client.get("/dashboard"),
    capitalOperations: () => client.get("/capital/operations"),
    // [C27] routes/subscription.ts
    subscription: () => client.get("/subscription"),
    subscriptionHistory: () => client.get("/subscription/history"),
    redeemCard: (code, proof) => client.post("/subscription/redeem", { code, ...proof }),
    previewOperation: (type, input) => client.post("/operations/preview", { type, input }),
    commitOperation: (previewId, idempotencyKey, proof) => client.post("/operations/commit", { previewId, idempotencyKey, ...proof }),
    approvals: (mine) => client.get(`/approvals${mine ? "?mine=1" : ""}`),
    approve: (pendingId, idempotencyKey, pin) => client.post("/approvals/approve", { pendingId, idempotencyKey, pin }),
    reject: (pendingId, reason, pin) => client.post("/approvals/reject", { pendingId, reason, pin }),
    cancelPending: (pendingId, note) => client.post("/approvals/cancel", { pendingId, ...(note ? { note } : {}) }),
    createBox: (body) => client.post("/boxes", body),
    updateBox: (body) => client.patch("/boxes", body),
    archiveBox: (accountId, archive, pin) => client.post("/boxes/archive", { accountId, archive, pin }),
    // C20 (routes/dollar.ts; the rate dialog uses PUT /rates of routes/admin.ts)
    dollar: () => client.get("/dollar"),
    dollarOperations: (q) => {
      const p = new URLSearchParams();
      for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== "") p.set(k, String(v));
      const qs = p.toString();
      return client.get(`/dollar/operations${qs ? `?${qs}` : ""}`);
    },
    setRate: (currency, buy, sell, proof) => client.put("/rates", { currency, buy, sell, ...proof }),
    // C21 (routes/currencies.ts)
    currencies: () => client.get("/currencies"),
    currencyOperations: (q) => {
      const p = new URLSearchParams();
      for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== "") p.set(k, String(v));
      const qs = p.toString();
      return client.get(`/currencies/operations${qs ? `?${qs}` : ""}`);
    },
    currencySettings: () => client.get("/currencies/settings"),
    addCurrency: (body) => client.post("/currencies", body),
    updateCurrency: (body) => client.patch("/currencies", body),
    deleteCurrency: (code, pin) => client.post("/currencies/delete", { code, pin }),
    // C22 (routes/customers.ts, routes/operations.ts)
    customers: (archived = false) => client.get(`/customers${archived ? "?archived=1" : ""}`),
    customerNameCheck: (name) => client.get(`/customers/check-name?name=${encodeURIComponent(name)}`),
    customerVoucher: (id, operationId, channel) => client.post(`/customers/${id}/voucher`, { operationId, channel }),
    customerStatement: (id, q) => {
      const p = new URLSearchParams();
      for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== "") p.set(k, String(v));
      const qs = p.toString();
      return client.get(`/customers/${encodeURIComponent(id)}/statement${qs ? `?${qs}` : ""}`);
    },
    createCustomer: (body, proof) => client.post("/customers", { ...body, ...proof }),
    updateCustomer: (body, pin) => client.patch("/customers", { ...body, pin }),
    archiveCustomer: (id, archive, pin) => client.post("/customers/archive", { id, archive, pin }),
    previewOperationWithNew: (type, input, newAccounts) => client.post("/operations/preview", { type, input, newAccounts }),
    commitOperations: (items, proof) => client.post("/operations/commit-batch", { items, ...proof }),
    // C23 (routes/transfers.ts)
    transfers: () => client.get("/transfers"),
    transferDetail: (id) => client.get(`/transfers/${encodeURIComponent(id)}`),
    transferCode: () => client.post("/transfers/code", {}),
    transferQuote: (body) => client.post("/transfers/quote", body),
    previewTransfer: (body) => client.post("/transfers/preview", body),
    commitTransfer: (previewId, idempotencyKey, acknowledgeDuplicateCode, proof) =>
      client.post("/transfers/commit", { previewId, idempotencyKey, ...(acknowledgeDuplicateCode ? { acknowledgeDuplicateCode: true } : {}), ...proof }),
    cancelTransfer: (transferId, reason, idempotencyKey, pin) => client.post("/transfers/cancel", { transferId, reason, idempotencyKey, pin }),
    transferPrinted: (id, channel) => client.post(`/transfers/${encodeURIComponent(id)}/print`, { channel }),
    // C24 (routes/journal.ts)
    journal: (q) => {
      const p = new URLSearchParams();
      for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== "" && v !== false) p.set(k, v === true ? "1" : String(v));
      return client.get(`/journal?${p.toString()}`);
    },
    journalDetail: (id) => client.get(`/journal/${encodeURIComponent(id)}`),
    journalCancel: (body, pin) => client.post("/journal/cancel", { ...body, pin }),
    journalEditPreview: (operationId, input, reason) => client.post("/journal/edit/preview", { operationId, input, reason }),
    journalEditCommit: (previewId, idempotencyKey, negative, pin) => client.post("/journal/edit/commit", { previewId, idempotencyKey, ...negative, pin }),
    // C25 (routes/profits.ts)
    profits: (q) => {
      const p = new URLSearchParams();
      for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== "") p.set(k, String(v));
      return client.get(`/profits?${p.toString()}`);
    },
    // C32 (routes/shifts.ts)
    shifts: (q) => client.get(`/shifts${qs(q)}`),
    shiftDrawers: (ownerUserId) => client.get(`/shifts/drawers?ownerUserId=${encodeURIComponent(ownerUserId)}`),
    openShift: (body) => client.post("/shifts/open", body),
    closeShift: (id, body) => client.post(`/shifts/${encodeURIComponent(id)}/close`, body),
    shiftReport: (id) => client.get(`/shifts/${encodeURIComponent(id)}/report`),
    shiftPrinted: (id, document, channel) => client.post(`/shifts/${encodeURIComponent(id)}/print`, { document, channel }),
    days: (q) => client.get(`/days${qs(q)}`),
    openDay: (body) => client.post("/days/open", body),
    closeDay: (id, body) => client.post(`/days/${encodeURIComponent(id)}/close`, body),
    dayReport: (id) => client.get(`/days/${encodeURIComponent(id)}/report`),
    dayPrinted: (id, channel) => client.post(`/days/${encodeURIComponent(id)}/print`, { document: "report", channel }),
    // [C34] Audit & Control (routes/audit.ts); the whole filtered set with all=1 (D-135)
    auditMovements: (q) => client.get(`/audit/movements${auditQs(q)}`),
    auditCustomers: (q) => client.get(`/audit/customers${auditQs(q)}`),
    auditPurchases: (q) => client.get(`/audit/purchases${auditQs(q)}`),
    auditSecurity: (q) => client.get(`/audit/security${auditQs(q)}`),
    auditSettings: (q) => client.get(`/audit/settings${auditQs(q)}`),
    journalUndo: (body, pin) => client.post("/journal/undo", { ...body, pin }),
    countHypotheses: (id) => client.get(`/cash-counts/${encodeURIComponent(id)}/hypotheses`),
    watchdogStatus: (state) => client.get(`/watchdog/status?state=${state}`),
    // [C33] Store (routes/store.ts)
    store: () => client.get("/store"),
    storeOperations: (q) => client.get(`/store/operations${qs(q)}`),
    storeBuyer: (id) => client.get(`/store/operations/${encodeURIComponent(id)}/buyer`),
    createWarehouse: (body) => client.post("/store/warehouses", body),
    updateWarehouse: (body) => client.patch("/store/warehouses", body),
    archiveWarehouse: (id, archive, proof) => client.post("/store/warehouses/archive", { id, archive, ...proof }),
    createProduct: (body) => client.post("/store/products", body),
    updateProduct: (body) => client.patch("/store/products", body),
    archiveProduct: (id, archive, proof) => client.post("/store/products/archive", { id, archive, ...proof }),
    storePreview: (body) => client.post("/store/preview", body),
    storeCommit: (previewId, idempotencyKey, proof) => client.post("/store/commit", { previewId, idempotencyKey, ...proof }),
    storeCancel: (operationId, reason, idempotencyKey, pin) => client.post("/store/cancel", { operationId, reason, idempotencyKey, pin }),
    storePrinted: (id, channel) => client.post(`/store/operations/${encodeURIComponent(id)}/print`, { channel }),
    // [C33] Installments (routes/installments.ts)
    installments: () => client.get("/installments"),
    installment: (id) => client.get(`/installments/${encodeURIComponent(id)}`),
    contractPreview: (body) => client.post("/installments/preview", body),
    contractCommit: (previewId, idempotencyKey, proof) => client.post("/installments/commit", { previewId, idempotencyKey, ...proof }),
    paymentPreview: (body) => client.post("/installments/payments/preview", body),
    paymentCommit: (previewId, idempotencyKey, proof) => client.post("/installments/payments/commit", { previewId, idempotencyKey, ...proof }),
    scheduleEdit: (body, proof) => client.post("/installments/schedule", { ...body, ...proof }),
    contractCancel: (contractId, reason, idempotencyKey, pin) => client.post("/installments/cancel", { contractId, reason, idempotencyKey, pin }),
    paymentCancel: (operationId, reason, idempotencyKey, pin) => client.post("/installments/payments/cancel", { operationId, reason, idempotencyKey, pin }),
    contractPrinted: (id, channel) => client.post(`/installments/${encodeURIComponent(id)}/print`, { channel }),
    receiptPrinted: (id, channel) => client.post(`/installments/payments/${encodeURIComponent(id)}/print`, { channel }),
    // [C35] documents
    documentPdf: (body) => client.postBlob("/documents/pdf", body),
    documentWords: (body) => client.post("/documents/words", body),
    officeLogo: () => client.get("/office/logo"),
    securityEmail: () => client.get("/security/email"),
    setSecurityEmail: (address, pin) => client.post("/security/email", { address, pin }),
    verifySecurityEmail: (code) => client.post("/security/email/verify", { code }),
    removeSecurityEmail: (pin) => client.post("/security/email/remove", { pin }),
    // [C38] B6 part 2
    backupState: () => client.get("/backup/state"),
    backupRegisterKey: (publicKey, totpCode, reason, pin) => client.post("/backup/key", { publicKey, totpCode, pin, ...(reason ? { reason } : {}) }),
    backupDownload: (pin) => client.postBlob("/backup/download", { pin }),
    backupCopyDownload: (id, pin) => client.postBlob(`/backup/copies/${encodeURIComponent(id)}/download`, { pin }),
    backupKeyCheck: () => client.post("/backup/key/check", {}),
    backupKeyVerify: (checkId, nonce) => client.post("/backup/key/verify", { checkId, nonce }),
    backupShareEnable: (keyId, share, totpCode, pin) => client.post("/backup/share", { keyId, share, totpCode, pin }),
    backupShareRemove: (totpCode, pin) => client.post("/backup/share/remove", { totpCode, pin }),
    backupShareRelease: (totpCode, pin) => client.post("/backup/share/release", { totpCode, pin }),
    datasets: () => client.get("/datasets"),
    datasetReset: (officeName, totpCode, pin) => client.post("/datasets/reset", { officeName, totpCode, acknowledged: true, pin }),
    datasetRollback: (version, totpCode, pin) => client.post("/datasets/rollback", { version, totpCode, pin }),
    datasetView: (version, pin) => client.post("/datasets/view", { version, pin }),
    datasetViewClose: () => client.post("/datasets/view/close", {}),
    approveCount: (id, body) => client.post(`/cash-counts/${encodeURIComponent(id)}/approve`, body),
    rejectCount: (id, body) => client.post(`/cash-counts/${encodeURIComponent(id)}/reject`, body),
  };
}
